import React from 'react';
import { format } from 'date-fns';
import { Calendar, Users, MapPin, DollarSign } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

type Item = any;

function isTourItem(item: any): boolean {
  if (!item || typeof item !== 'object') return false;
  if (item.type === 'tour_reservation') return true;

  return Boolean(
    (item.tourTitle || item.tourDate || item.quantityPeople || item.pricePerPerson || item.passengers) &&
    (item.tourTitle || item.tourDate)
  );
}

function normalizeItems(raw: any): any[] {
  if (!raw) return [];

  const extractImageUrl = (img: any): string | null => {
    if (!img) return null;
    if (typeof img === 'string') return img;
    if (Array.isArray(img) && img.length > 0) return extractImageUrl(img[0]);
    if (typeof img === 'object') {
      const keys = Object.keys(img);
      if (keys.length > 0) return img[keys[0]];
    }
    return null;
  };

  if (Array.isArray(raw)) {
    return raw
      .map((item: any) => {
        // Check if it's a tour item first
        if (isTourItem(item)) return item;

        const title =
          item?.product_title ??
          item?.title ??
          item?.products?.title ??
          item?.product?.title ??
          item?.product_name ??
          item?.name;

        const rawImageUrl =
          item?.image_url ??
          item?.images_url ??
          item?.image_urls ??
          item?.products?.image_url ??
          item?.products?.image_urls ??
          item?.product?.image_url ??
          item?.thumbnail ??
          item?.image;

        const imageUrl = extractImageUrl(rawImageUrl);

        const quantity = Number(item?.quantity ?? item?.qty ?? item?.quantityPeople ?? 1) || 1;
        const unitPrice = Number(item?.unit_price ?? item?.unitPrice ?? item?.price ?? item?.pricePerPerson ?? 0) || 0;

        // Must have at least a title to be valid
        if (!title) return null;

        return {
          ...item,
          id: item?.id || item?.product_id || `item-${Date.now()}`,
          product_title: title,
          product_name: item?.product_name ?? title,
          title: title,
          image_url: imageUrl,
          quantity: quantity,
          unit_price: unitPrice,
          product: {
            ...(item?.product || {}),
            id: item?.product_id ?? item?.product?.id ?? undefined,
            title: title,
            image_url: imageUrl,
          },
        };
      })
      .filter(Boolean);
  }

  if (typeof raw === 'string') {
    try {
      return normalizeItems(JSON.parse(raw));
    } catch {
      return [];
    }
  }

  if (typeof raw === 'object') {
    if (raw.type === 'tour_reservation') return [raw];
    if (Array.isArray(raw.items)) return normalizeItems(raw.items);
    if (Array.isArray(raw.items_list)) return normalizeItems(raw.items_list);
    return [];
  }

  return [];
}

function extractTourPassengers(items: any[]): any[] {
  const passengers: any[] = [];

  items.forEach((item) => {
    if (isTourItem(item) && Array.isArray(item.passengers)) {
      passengers.push(...item.passengers);
    }
  });

  return passengers;
}

const ItemRow: React.FC<{ item: Item; onItemClick?: (productId: string) => void }> = ({ item, onItemClick }) => {
  const isTour = isTourItem(item);
  const title = isTour
    ? item.tourTitle || item.destination || item.title || item.name || 'Excursión'
    : item.title || item.name || item.product_title || item.product?.title || item.product_name || 'Item';

  const quantity = Number(item.quantity ?? item.qty ?? item.quantityPeople ?? 1);
  const unitPrice = Number(item.unit_price ?? item.price ?? item.unitPrice ?? item.pricePerPerson ?? 0);
  const lineTotal = unitPrice * quantity;
  const thumbnail = item.thumbnail || item.image_url || item.image || item.product?.image_url || null;
  const productId = item.product_id ?? item.product?.id ?? item.products?.id ?? null;

  const handleTitleClick = () => {
    if (productId && onItemClick && !isTour) {
      onItemClick(productId);
    }
  };

  return (
    <div className="flex flex-wrap gap-3 rounded-lg border bg-muted/40 p-3 items-start">
      {/* Photo */}
      {thumbnail ? (
        <img src={thumbnail} alt={title} className="h-14 w-14 rounded object-cover flex-shrink-0" />
      ) : (
        <div className="flex h-14 w-14 items-center justify-center rounded bg-muted/30 text-sm text-muted-foreground flex-shrink-0">
          IMG
        </div>
      )}

      {/* Title and Details */}
      <div className="min-w-0 flex-1 min-w-[200px]">
        <div
          className={`break-words text-sm font-semibold text-primary line-clamp-2${
            productId && !isTour && onItemClick ? ' cursor-pointer hover:opacity-70 transition-opacity' : ''
          }`}
          onClick={handleTitleClick}
          role={productId && !isTour && onItemClick ? 'button' : undefined}
          tabIndex={productId && !isTour && onItemClick ? 0 : undefined}
          onKeyDown={(e) => {
            if (productId && !isTour && onItemClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleTitleClick();
            }
          }}
        >
          {title}
        </div>
        <div className="break-words text-xs text-muted-foreground mt-1">
          <span className="inline">{quantity}x</span> · <span className="tabular-nums">{formatCurrency(unitPrice)}</span> c/u
        </div>
      </div>

      {/* Price - wraps to next line if constrained */}
      <div className="flex flex-col items-start sm:items-end whitespace-nowrap">
        <div className="text-lg font-bold tabular-nums text-primary">{formatCurrency(lineTotal)}</div>
      </div>
    </div>
  );
};

const TourReservationRow: React.FC<{ item: Item }> = ({ item }) => {
  const tourDate = item.tourDate ? new Date(item.tourDate) : null;
  const totalPrice = item.totalPrice ?? item.total_price ?? item.price ?? 0;
  const pricePerPerson = item.pricePerPerson ?? item.price_per_person ?? null;
  const quantityPeople = item.quantityPeople ?? item.people ?? item.qty ?? 1;
  const pickupAddress = item.pickupAddress || item.address || null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold text-primary">
            {item.tourTitle || item.destination || 'Excursión'}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Reserva de tour</div>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-lg font-bold tabular-nums text-primary sm:text-xl">
            {formatCurrency(totalPrice)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        {tourDate && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
            <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-muted-foreground">Fecha</div>
              <div className="truncate text-sm font-medium">{format(tourDate, 'dd MMM yyyy')}</div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">Personas</div>
            <div className="text-sm font-medium">
              {quantityPeople} {quantityPeople === 1 ? 'persona' : 'personas'}
            </div>
          </div>
        </div>

        {pricePerPerson !== null && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
            <DollarSign className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-muted-foreground">Por persona</div>
              <div className="text-sm font-medium">{formatCurrency(pricePerPerson)}</div>
            </div>
          </div>
        )}

        {pickupAddress && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
            <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-muted-foreground">Punto de partida</div>
              <div className="truncate text-sm font-medium">{pickupAddress}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const OrderItemsList: React.FC<{ items?: any; onItemClick?: (productId: string) => void }> = ({ items, onItemClick }) => {
  console.log('📋 [OrderItemsList] Received items prop:', items);
  
  const normalized = normalizeItems(items);
  
  console.log('📋 [OrderItemsList] After normalization, items count:', normalized.length);
  console.log('📋 [OrderItemsList] Normalized items:', normalized);

  if (!normalized || normalized.length === 0) {
    console.warn('⚠️ [OrderItemsList] No items after normalization, showing empty state');
    return <div className="text-sm text-muted-foreground">No hay items en este pedido.</div>;
  }

  const tourPassengers = extractTourPassengers(normalized);

  return (
    <>
      {tourPassengers.length > 0 && (
        <div className="mb-4 min-w-0 overflow-hidden rounded border-l-4 border-primary bg-primary/10 p-3">
          <div className="mb-1 text-xs font-bold text-primary">Pasajeros de la excursión</div>
          <ul className="space-y-1">
            {tourPassengers.map((passenger, index) => (
              <li key={passenger.id || index} className="flex min-w-0 items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-primary/60" />
                <span className="truncate font-medium">{passenger.name}</span>
                <span className="break-all text-xs text-muted-foreground">
                  {passenger.documentNumber
                    ? `DNI: ${passenger.documentNumber}`
                    : passenger.dniNumber
                      ? `DNI: ${passenger.dniNumber}`
                      : passenger.passportNumber
                        ? `Pasaporte: ${passenger.passportNumber}`
                        : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2 min-w-0 overflow-x-hidden">
        {normalized.map((item: any, index: number) =>
          isTourItem(item) ? (
            <TourReservationRow item={item} key={item.id || item.productId || index} />
          ) : (
            <ItemRow item={item} onItemClick={onItemClick} key={item.id || item.product_id || item.product?.id || index} />
          )
        )}
      </div>
    </>
  );
};

export default OrderItemsList;